import { all, select, put, call, take, fork, race, takeEvery } from 'redux-saga/effects';

import { syncResource } from '../sync/actions';
import { ensureSynced } from '../sync/sagas';
import {
    SELECT_TASK,
    selectTask,
    SELECT_TASK_ITEM,
    selectTaskItem,
    SELECT_NEXT_TASK_ITEM,
    TASK_VIEW_OPENED,
    SELECT_NEXT_TASK,
    updateViewState,
    SYNC_PACKAGE,
    selectPackage,
    SYNC_AND_SELECT_PACKAGE,
} from './actions';
import { startAlert } from '../alert/actions';
import {
    GENERATION_STATUS_FAILED,
    GENERATION_STATUS_HW_ON_DEMAND_PLACEHOLDER,
} from '../content/actions';
import { openTaskView, openSkillDeliveryView } from 'phoenix2/state/views/sagas';
import { request } from 'phoenix2/state/remote/sagas';
import { startWAC } from 'phoenix2/state/wac/actions';
import { getTaskState, getTaskItemState } from 'phoenix2/state/progress/selectors';
import {
    getPackageList,
    getPackage,
    makeSelectPackageStatus,
    itemStatus,
    getPackageType,
    getPackageGenerationStatus,
} from 'phoenix2/state/content/selectors';
import { wCall, throttledTakeEvery, asyncSleep } from 'phoenix2/utils';
import { getSessionType } from 'phoenix2/state/session/selectors';
import { HOMEWORK, SOLO } from 'phoenix2/state/session/session-types';
import { isImmutableException } from 'phoenix2/utils/exceptions';
import { selectCurrentViewType } from 'phoenix2/state/views/selectors';
import { analyticsHit, startParallelKpi, endParallelKpi, kpiNames } from '../analytics/actions';
import { triggerOnBoardingVideoIfRequired } from 'phoenix2/state/tutorial/tutorial-sagas/onboarding-video';
import { startExplicitWaiting, stopExplicitWaitingWithDelay } from '../wait/actions';
import { getTask } from 'phoenix2/state/content/selectors_ts';
import { isFTQQuizComplete, isFTQTask } from 'phoenix2/utils/ftqtask';
import { skipFTQ } from 'phoenix2/state/ftq-task/sagas';

export function* watchSyncAndSelectPackage() {
    yield takeEvery(SYNC_AND_SELECT_PACKAGE, doSyncAndSelectPackage);
}

function* doSyncAndSelectPackage(action) {
    const { viewId, packageId, selected } = action;

    // Ensure the package is synced before we continue
    yield call(doSyncPackage, { packageId, showWaiting: true });

    // Open the panel on the package list
    yield put(selectPackage(viewId, packageId, selected));
}

/**
 * Sync the package tasks and the package task states from the server. If the
 * package is not generated yet it will also ensure that the package is generated.
 *
 * @param action is an object containing { packageId, showWaiting }
 */
export function* doSyncPackage(action) {
    const { packageId, showWaiting } = action;
    const state = yield select();
    let generationStatus = getPackageGenerationStatus(state, packageId);

    if (generationStatus === GENERATION_STATUS_HW_ON_DEMAND_PLACEHOLDER) {
        if (showWaiting) {
            yield put(startExplicitWaiting('Creating homework...', true));
        }
        // Begin timing the generation
        yield put(startParallelKpi(kpiNames.FETCH_ON_DEMAND_PACKAGE, packageId));
        // Refetch this package
        yield call(ensureSynced, 'package', packageId, true);
        // get the new state again, now that we should have generated the package
        const newState = yield select();
        generationStatus = getPackageGenerationStatus(newState, packageId);

        // Stop timing and send the analytic event
        const packageType = getPackageType(newState, packageId);
        yield put(
            endParallelKpi(kpiNames.FETCH_ON_DEMAND_PACKAGE, packageId, {
                packageType: packageType,
                generationStatus: generationStatus,
            }),
        );
        if (showWaiting) {
            yield put(stopExplicitWaitingWithDelay(1));
        }
    }
    if (generationStatus !== GENERATION_STATUS_FAILED) {
        yield put(syncResource('packageTasks', packageId));
        yield put(syncResource('packageTaskStates', packageId));
    }
}

/**
 * When a SELECT_TASK action is fired it forks openTaskView. It then waits
 * for either the task to have opened or for 3s to implement a debounce.
 */
export function* watchSelectTask() {
    while (true) {
        const action = yield take(SELECT_TASK);
        yield fork(openTaskView, action);
        // Debounce the UI click by waiting for the task view to open
        // or 3s to elapse
        yield race({
            viewOpened: take(TASK_VIEW_OPENED),
            timeout: asyncSleep(3),
        });
    }
}

export function* watchSyncPackage() {
    yield takeEvery(SYNC_PACKAGE, doSyncPackage);
}

/**
 * Starts a new task item activity for an item. If null is returned then
 * there was a failure starting the task item.
 * @param item The task item to create the activity for
 * @param method The method to get the question activity with - this can be:
 *   - NEW        Get a new activity for this task item.
 *   - RESUMABLE  Resume the last activity for this task item if it is in a
 *                resumable state or get a new one.
 *   - LAST       Retrieve the last activity for this task item whatever state
 *                it is in. If there is no last one then an error is returned.
 * @returns item Info, or null if failed
 */
export function* startTaskItemActivity(item, method, activityType) {
    // For first time users, trigger the student on-boarding video:
    yield call(triggerOnBoardingVideoIfRequired, item);

    let info;
    try {
        info = yield wCall(request, 'startTaskItem', item.get('id'), method, activityType);
    } catch (exc) {
        if (!isImmutableException(exc)) {
            throw exc;
        }

        switch (exc.get('type')) {
            case 'PendingWAC':
                yield put(startWAC(item));
                return null;
            case 'ConnectionClosed':
                // Connection will show as closed and user can try again.
                return null;
            default:
                const sessionType = yield select(getSessionType);
                const workType = sessionType === HOMEWORK ? 'Homework' : 'work';
                const isSolo = sessionType === SOLO;
                yield put(
                    startAlert({
                        title: 'Problem loading question',
                        message:
                            'There has been a problem loading this question. ' +
                            (isSolo
                                ? 'Please move on'
                                : `Please move on, complete the rest of your Sparx ${workType},`) +
                            ' and then retry this question later.',
                    }),
                );
                yield put(
                    analyticsHit('event', {
                        eventCategory: 'content error',
                        eventAction: 'skill',
                        eventLabel: {
                            skill_id: item.get('id'),
                        },
                    }),
                );
                return null;
        }
    }
    return info;
}

export function* doSelectTaskItem(action) {
    const { item, jumpToStage } = action;

    // In fast track quiz tasks we want to skip the quiz if a task item is opened first
    const taskId = item.get('task_id');
    const task = yield select(getTask, taskId);
    if (task && isFTQTask(task)) {
        const taskState = yield select(getTaskState, taskId);
        if (!isFTQQuizComplete(taskState)) {
            // call this synchronously, so that the state has been updated before opening the question,
            // so we can show the onboarding tutorial
            yield call(skipFTQ, taskId);
        }
    }

    const info = yield call(startTaskItemActivity, item, 'NEW');
    if (!info) {
        return;
    }

    // Remember that this is the most recently started task item.
    // Used to determine which task item to animate on return to task view.
    const itemId = item.get('id');
    const taskItemState = yield select(getTaskItemState, itemId);
    yield put(
        updateViewState('task', {
            mostRecentTaskItemId: itemId,
            mostRecentTaskItemStatus: itemStatus(taskItemState),
        }),
    );

    // Open the Skill Delivery View.
    const currentView = yield select(selectCurrentViewType);
    const openMethod = currentView === 'wac' ? 'replace' : 'push';
    yield call(
        openSkillDeliveryView,
        info,
        item.get('id'),
        openMethod,
        undefined,
        undefined,
        jumpToStage,
    );
}

export function* watchSelectTaskItem() {
    yield throttledTakeEvery(SELECT_TASK_ITEM, doSelectTaskItem, 0);
}

/**
 * Selects the next uncompleted task item for the provided package. (This
 * enables a useful keyboard shortcut for the QA team)
 * @param action
 */
function* doSelectNextTaskItem(action) {
    const { taskId } = action;
    const task = yield select(getTask, taskId);

    if (!task) {
        return;
    }
    const items = task.get('items');

    if (!items) {
        return;
    }

    for (const item of items) {
        const itemId = item.get('id');
        const itemState = yield select(getTaskItemState, itemId);

        if (!itemState) {
            // Do nothing - task item states not loaded yet, so shortcut cannot
            // yet be used:
            return;
        }

        if (!itemState.get('done', false)) {
            yield put(selectTaskItem(item));
            return;
        }
    }
}

export function* watchSelectNextTaskItem() {
    yield throttledTakeEvery(SELECT_NEXT_TASK_ITEM, doSelectNextTaskItem, 0);
}

/**
 * Selects the next uncompleted task from all the packages that match the
 * package filter in the action. (This enables a useful keyboard shortcut
 * for the QA team)
 * @param action
 */
function* doSelectNextTask(action) {
    const { filter } = action;

    // Get the list of packages (in the right order):
    const packageList = yield select(getPackageList);
    if (!packageList) {
        return;
    }

    const selectPackageStatus = makeSelectPackageStatus();

    for (const packageId of packageList) {
        const pkg = yield select(getPackage, packageId);
        if (!pkg) {
            continue;
        }

        // Do not look at packages that do not match the filter
        if (pkg.get('package_type') !== filter) {
            continue;
        }

        const packageStatus = yield select(selectPackageStatus, packageId);
        if (!packageStatus || packageStatus.complete || packageStatus.generationFailed) {
            continue;
        }

        // Incomplete package, so fetch the task info if necessary:
        yield wCall(syncPackageTasksAndStates, packageId);

        for (const taskId of pkg.get('task_ids')) {
            const task = yield select(getTaskState, taskId);

            if (!task) {
                continue;
            }

            // Choose the first incomplete task:
            if (!task || !task.get('complete')) {
                yield put(selectTask(taskId));
                return;
            }
        }
    }
}

function* syncPackageTasksAndStates(packageId) {
    yield all([
        call(ensureSynced, 'packageTasks', packageId, true),
        call(ensureSynced, 'packageTaskStates', packageId, true),
    ]);
}

export function* watchSelectNextTask() {
    yield throttledTakeEvery(SELECT_NEXT_TASK, doSelectNextTask, 0);
}
